"use client";
import { cn } from "@/lib/tailwind";
import { ImageProps } from "next/image";
import Image from "@/components/ui/imageV2/ImageV2";
import { DetailedHTMLProps, HTMLAttributes } from "react";
import { CLink } from "../customLink/CustomLink";
import VisibilitySensor from "../VisibilitySensor";
import PortableText from "../PortableText";
import { PortableTextBlock } from "next-sanity";

export type IImageCardProps = {
  imageProps: ImageProps;
  containerProps?: Omit<
    DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
    "children"
  >;
  action: {
    cta?: React.ReactNode;
    title?: React.ReactNode;
    titleClassName?: string;
    description?: React.ReactNode;
    subDescription?: React.ReactNode;
    containerProps?: Omit<
      DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
      "children"
    >;
  };
  url?: string;
  onClick?: () => void;
  onVisible?: (isVisible: boolean) => void;
};

const ImageCardContainer = ({ action, imageProps, containerProps = {}, url }: IImageCardProps) => {
  return (
    <div {...containerProps} className={cn("relative h-full w-full")}>
      <Image {...imageProps} className={cn(imageProps?.className)} />
      <div className="absolute bottom-0 h-1/3 w-full bg-gradient-to-b from-[rgba(0,0,0,0)] to-[rgba(0,0,0,0.55)] md:h-[211px]">
        <div
          {...action?.containerProps}
          className={cn(
            "absolute bottom-4 left-3 w-full min-w-0 max-w-[597px] sm:bottom-6 sm:left-6 lg:bottom-8 lg:left-8 rtl:bottom-4 rtl:right-4 md:rtl:bottom-6 rtl:md:right-6",
            action?.containerProps?.className,
          )}
        >
          {action?.title &&
            (typeof action?.title === "string" ? (
              <div className="max-w-[387px] min-w-0">
                <h2 className={cn("mb-2 break-words text-base font-bold text-white md:text-[32px]", action?.titleClassName)}>
                  {action?.title}
                </h2>
              </div>
            ) : (
              action?.title
            ))}
          {action?.description && (
            <div className="max-w-[377px] min-w-0 break-words text-xs text-white md:text-lg">
              {typeof action.description === "string" ? (
                <p className="whitespace-normal break-words">{action.description}</p>
              ) : (
                <PortableText value={action?.description as unknown as PortableTextBlock} />
              )}
            </div>
          )}
          {action?.subDescription && (
            <div className="mt-1 max-w-[377px] min-w-0 text-[0.625rem] text-white sm:text-sm md:text-base">
              <PortableText value={action?.subDescription as unknown as PortableTextBlock} />
            </div>
          )}
          {action?.cta && (
            <div className="mt-3 sm:mt-4">
              {typeof action?.cta === "string" ? <p>{action?.cta}</p> : action?.cta}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ImageCard = ({ onClick = () => {}, onVisible, ...rest }: IImageCardProps) => {
  return (
    <div className={rest?.containerProps?.className} style={rest?.containerProps?.style} onClick={onClick}>
      {rest?.url ? (
        <CLink href={rest?.url} className="h-full w-full">
          <ImageCardContainer {...rest} />
        </CLink>
      ) : (
        <ImageCardContainer {...rest} />
      )}

      {onVisible && (
        <div className="w-full items-center justify-center">
          <VisibilitySensor onVisible={onVisible}>
            <div />
          </VisibilitySensor>
        </div>
      )}
    </div>
  );
};

export default ImageCard;
